import React, { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Tag,
  Space,
  Card,
  Empty,
  InputNumber,
  Select,
  Typography,
  Tooltip,
  App,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ExclamationCircleOutlined,
  ShopOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import {
  useGetSuppliersQuery,
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
  useDeleteSupplierMutation,
  useGetSupplierItemsQuery,
  useCreateSupplierItemMutation,
  useUpdateSupplierItemMutation,
  useDeleteSupplierItemMutation,
} from '../../services/suppliersApi';
import { useGetInventoryItemsQuery } from '../../services/inventoryApi';
import type {
  Supplier,
  CreateSupplierRequest,
  UpdateSupplierRequest,
  SupplierItem,
  CreateSupplierItemRequest,
  UpdateSupplierItemRequest,
} from '../../types/inventory';

const { Text } = Typography;
const { TextArea } = Input;

// =====================================================
// Supplier Items Sub-Table (Expanded Row)
// =====================================================

interface SupplierItemsTableProps {
  supplier: Supplier;
}

const SupplierItemsTable: React.FC<SupplierItemsTableProps> = ({ supplier }) => {
  const { isMobile } = useResponsive();
  const { message, modal } = App.useApp();

  const { data: supplierItems = [], isLoading: loadingItems } = useGetSupplierItemsQuery(supplier.id);
  const { data: inventoryItems = [] } = useGetInventoryItemsQuery();
  const [createSupplierItem, { isLoading: creating }] = useCreateSupplierItemMutation();
  const [updateSupplierItem, { isLoading: updating }] = useUpdateSupplierItemMutation();
  const [deleteSupplierItem] = useDeleteSupplierItemMutation();

  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SupplierItem | null>(null);
  const [itemForm] = Form.useForm();

  const handleOpenCreateItem = () => {
    setEditingItem(null);
    itemForm.resetFields();
    itemForm.setFieldsValue({ isPreferred: false });
    setItemModalOpen(true);
  };

  const handleOpenEditItem = (record: SupplierItem) => {
    setEditingItem(record);
    itemForm.setFieldsValue({
      supplierPrice: record.supplierPrice,
      supplierSKU: record.supplierSKU,
      isPreferred: record.isPreferred,
    });
    setItemModalOpen(true);
  };

  const handleCloseItemModal = () => {
    setItemModalOpen(false);
    setEditingItem(null);
    itemForm.resetFields();
  };

  const handleSaveItem = async () => {
    try {
      const values = await itemForm.validateFields();

      if (editingItem) {
        const updateData: UpdateSupplierItemRequest = {
          supplierPrice: values.supplierPrice,
          supplierSKU: values.supplierSKU,
          isPreferred: values.isPreferred ?? false,
        };
        await updateSupplierItem({ id: editingItem.id, data: updateData }).unwrap();
        message.success('Articulo de proveedor actualizado');
      } else {
        const createData: CreateSupplierItemRequest = {
          supplierId: supplier.id,
          inventoryItemId: values.inventoryItemId,
          supplierPrice: values.supplierPrice,
          supplierSKU: values.supplierSKU,
          isPreferred: values.isPreferred ?? false,
        };
        await createSupplierItem(createData).unwrap();
        message.success('Articulo agregado al proveedor');
      }

      handleCloseItemModal();
    } catch (error: any) {
      if (error?.errorFields) return; // validation error
      message.error(error?.data?.message || 'Error al guardar el articulo');
    }
  };

  const handleDeleteItem = (record: SupplierItem) => {
    modal.confirm({
      title: 'Eliminar Articulo',
      icon: <ExclamationCircleOutlined />,
      content: `¿Esta seguro de eliminar "${record.itemName}" de este proveedor?`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteSupplierItem(record.id).unwrap();
          message.success('Articulo eliminado del proveedor');
        } catch (error: any) {
          message.error(error?.data?.message || 'Error al eliminar el articulo');
        }
      },
    });
  };

  // Filter out inventory items that are already assigned to this supplier
  const availableInventoryItems = useMemo(() => {
    const assignedIds = new Set(supplierItems.map((si) => si.inventoryItemId));
    return inventoryItems.filter((item) => !assignedIds.has(item.id) && !item.isPreparation);
  }, [inventoryItems, supplierItems]);

  const itemColumns: ColumnsType<SupplierItem> = [
    {
      title: 'Articulo',
      dataIndex: 'itemName',
      key: 'itemName',
      ellipsis: true,
    },
    {
      title: 'Precio Proveedor',
      dataIndex: 'supplierPrice',
      key: 'supplierPrice',
      width: 140,
      align: 'right',
      render: (price: number) => `$${price.toFixed(2)}`,
    },
    {
      title: 'SKU Proveedor',
      dataIndex: 'supplierSKU',
      key: 'supplierSKU',
      width: 140,
      render: (sku: string | undefined) => sku || '-',
    },
    {
      title: 'Preferido',
      dataIndex: 'isPreferred',
      key: 'isPreferred',
      width: 100,
      align: 'center',
      render: (isPreferred: boolean) => (
        <Tag color={isPreferred ? 'gold' : 'default'}>
          {isPreferred ? 'Si' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_: unknown, record: SupplierItem) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenEditItem(record)}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteItem(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Text strong style={{ fontSize: 13 }}>
          Articulos de {supplier.name}
        </Text>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={handleOpenCreateItem}
        >
          Agregar Articulo
        </Button>
      </div>

      <Table
        columns={itemColumns}
        dataSource={supplierItems}
        loading={loadingItems}
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ x: 500 }}
        locale={{
          emptyText: (
            <Empty
              description="No hay articulos asignados a este proveedor"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />

      {/* Supplier Item Modal */}
      <Modal
        title={editingItem ? 'Editar Articulo de Proveedor' : 'Agregar Articulo a Proveedor'}
        open={itemModalOpen}
        onOk={handleSaveItem}
        onCancel={handleCloseItemModal}
        confirmLoading={creating || updating}
        okText={editingItem ? 'Guardar' : 'Agregar'}
        cancelText="Cancelar"
        width={isMobile ? '95%' : 480}
        destroyOnClose
      >
        <Form
          form={itemForm}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          {!editingItem && (
            <Form.Item
              name="inventoryItemId"
              label="Articulo de Inventario"
              rules={[{ required: true, message: 'Seleccione un articulo' }]}
            >
              <Select
                placeholder="Seleccione un articulo"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    .includes(input.toLowerCase()) ?? false
                }
              >
                {availableInventoryItems.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name} ({item.unit})
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="supplierPrice"
            label="Precio del Proveedor"
            rules={[{ required: true, message: 'Ingrese el precio' }]}
          >
            <InputNumber
              prefix="$"
              min={0}
              step={1}
              precision={2}
              style={{ width: '100%' }}
              placeholder="0.00"
            />
          </Form.Item>

          <Form.Item
            name="supplierSKU"
            label="SKU del Proveedor"
          >
            <Input placeholder="Codigo del proveedor (opcional)" />
          </Form.Item>

          <Form.Item
            name="isPreferred"
            label="Proveedor Preferido"
            valuePropName="checked"
          >
            <Switch checkedChildren="Si" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// =====================================================
// Main Suppliers Tab
// =====================================================

const SuppliersTab: React.FC = () => {
  const { isMobile } = useResponsive();
  const { message, modal } = App.useApp();

  // API hooks
  const { data: suppliers = [], isLoading } = useGetSuppliersQuery();
  const [createSupplier, { isLoading: creatingSupplier }] = useCreateSupplierMutation();
  const [updateSupplier, { isLoading: updatingSupplier }] = useUpdateSupplierMutation();
  const [deleteSupplier] = useDeleteSupplierMutation();

  // Local state
  const [searchText, setSearchText] = useState('');
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [supplierForm] = Form.useForm();

  // Filtered suppliers
  const filteredSuppliers = useMemo(() => {
    if (!searchText.trim()) return suppliers;
    const search = searchText.toLowerCase();
    return suppliers.filter(
      (s) =>
        s.name.toLowerCase().includes(search) ||
        s.contactName?.toLowerCase().includes(search) ||
        s.phone?.toLowerCase().includes(search) ||
        s.email?.toLowerCase().includes(search)
    );
  }, [suppliers, searchText]);

  // Supplier Modal handlers
  const handleOpenCreateSupplier = () => {
    setEditingSupplier(null);
    supplierForm.resetFields();
    supplierForm.setFieldsValue({ isActive: true });
    setSupplierModalOpen(true);
  };

  const handleOpenEditSupplier = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    supplierForm.setFieldsValue({
      name: supplier.name,
      contactName: supplier.contactName,
      phone: supplier.phone,
      email: supplier.email,
      address: supplier.address,
      notes: supplier.notes,
      isActive: supplier.isActive,
    });
    setSupplierModalOpen(true);
  };

  const handleCloseSupplierModal = () => {
    setSupplierModalOpen(false);
    setEditingSupplier(null);
    supplierForm.resetFields();
  };

  const handleSaveSupplier = async () => {
    try {
      const values = await supplierForm.validateFields();

      if (editingSupplier) {
        const updateData: UpdateSupplierRequest = {
          name: values.name,
          contactName: values.contactName,
          phone: values.phone,
          email: values.email,
          address: values.address,
          notes: values.notes,
          isActive: values.isActive ?? true,
        };
        await updateSupplier({ id: editingSupplier.id, data: updateData }).unwrap();
        message.success('Proveedor actualizado correctamente');
      } else {
        const createData: CreateSupplierRequest = {
          name: values.name,
          contactName: values.contactName,
          phone: values.phone,
          email: values.email,
          address: values.address,
          notes: values.notes,
          isActive: values.isActive ?? true,
        };
        await createSupplier(createData).unwrap();
        message.success('Proveedor creado correctamente');
      }

      handleCloseSupplierModal();
    } catch (error: any) {
      if (error?.errorFields) return; // validation error
      message.error(error?.data?.message || 'Error al guardar el proveedor');
    }
  };

  const handleDeleteSupplier = (supplier: Supplier) => {
    const hasItems = supplier.itemCount > 0;

    modal.confirm({
      title: 'Eliminar Proveedor',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>¿Esta seguro de eliminar al proveedor "{supplier.name}"?</p>
          {hasItems && (
            <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
              Este proveedor tiene {supplier.itemCount} articulo(s) y podria tener ordenes de compra asociadas. Esta accion no se puede deshacer.
            </p>
          )}
        </div>
      ),
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteSupplier(supplier.id).unwrap();
          message.success('Proveedor eliminado correctamente');
        } catch (error: any) {
          message.error(error?.data?.message || 'Error al eliminar el proveedor');
        }
      },
    });
  };

  // Table columns
  const columns: ColumnsType<Supplier> = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Contacto',
      dataIndex: 'contactName',
      key: 'contactName',
      ellipsis: true,
      render: (contact: string | undefined) => contact || '-',
    },
    {
      title: 'Telefono',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (phone: string | undefined) => phone || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      ellipsis: true,
      render: (email: string | undefined) => email || '-',
    },
    {
      title: 'Articulos',
      dataIndex: 'itemCount',
      key: 'itemCount',
      width: 90,
      align: 'center',
      render: (count: number) => count,
    },
    {
      title: 'Activo',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      align: 'center',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Si' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_: unknown, record: Supplier) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditSupplier(record);
              }}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSupplier(record);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Mobile card renderer
  const renderMobileCard = (supplier: Supplier) => (
    <Card
      key={supplier.id}
      style={{ marginBottom: 12 }}
      size="small"
      actions={[
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => handleOpenEditSupplier(supplier)}
          size="small"
          key="edit"
        >
          Editar
        </Button>,
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteSupplier(supplier)}
          size="small"
          key="delete"
        >
          Eliminar
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <ShopOutlined />
            <Text strong>{supplier.name}</Text>
          </Space>
          <Tag color={supplier.isActive ? 'green' : 'red'}>
            {supplier.isActive ? 'Activo' : 'Inactivo'}
          </Tag>
        </div>

        {supplier.contactName && (
          <div style={{ fontSize: 13 }}>
            <UserOutlined style={{ marginRight: 6, color: 'var(--color-neutral-text)' }} />
            {supplier.contactName}
          </div>
        )}

        {supplier.phone && (
          <div style={{ fontSize: 13 }}>
            <PhoneOutlined style={{ marginRight: 6, color: 'var(--color-neutral-text)' }} />
            {supplier.phone}
          </div>
        )}

        {supplier.email && (
          <div style={{ fontSize: 13 }}>
            <MailOutlined style={{ marginRight: 6, color: 'var(--color-neutral-text)' }} />
            {supplier.email}
          </div>
        )}

        <div style={{ fontSize: 13, color: 'var(--color-neutral-text)' }}>
          {supplier.itemCount} articulo(s)
        </div>

        {/* Inline Supplier Items for mobile */}
        <SupplierItemsTable supplier={supplier} />
      </Space>
    </Card>
  );

  return (
    <>
      <div style={{ padding: isMobile ? 0 : '16px 0' }}>
        {/* Toolbar */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <Input
            placeholder="Buscar por nombre, contacto..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ maxWidth: isMobile ? '100%' : 300, flex: 1 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenCreateSupplier}
          >
            Nuevo Proveedor
          </Button>
        </div>

        {/* Content: Table or Mobile Cards */}
        {isMobile ? (
          isLoading ? (
            <div style={{ textAlign: 'center', padding: 20 }}>Cargando...</div>
          ) : filteredSuppliers.length > 0 ? (
            <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
              {filteredSuppliers.map((s) => renderMobileCard(s))}
            </div>
          ) : (
            <Empty
              description="No hay proveedores registrados"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: 40 }}
            />
          )
        ) : (
          <Table
            columns={columns}
            dataSource={filteredSuppliers}
            loading={isLoading}
            rowKey="id"
            size="middle"
            scroll={{ x: 800 }}
            pagination={{
              pageSize: 15,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} proveedores`,
            }}
            expandable={{
              expandedRowRender: (record: Supplier) => (
                <SupplierItemsTable supplier={record} />
              ),
            }}
            locale={{
              emptyText: (
                <Empty
                  description="No hay proveedores registrados"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
          />
        )}
      </div>

      {/* Supplier Create/Edit Modal */}
      <Modal
        title={editingSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor'}
        open={supplierModalOpen}
        onOk={handleSaveSupplier}
        onCancel={handleCloseSupplierModal}
        confirmLoading={creatingSupplier || updatingSupplier}
        okText={editingSupplier ? 'Guardar' : 'Crear'}
        cancelText="Cancelar"
        width={isMobile ? '95%' : 560}
        destroyOnClose
      >
        <Form
          form={supplierForm}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="name"
            label="Nombre del Proveedor"
            rules={[{ required: true, message: 'Ingrese el nombre del proveedor' }]}
          >
            <Input placeholder="Nombre del proveedor" />
          </Form.Item>

          <Form.Item
            name="contactName"
            label="Nombre de Contacto"
          >
            <Input placeholder="Persona de contacto (opcional)" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Telefono"
          >
            <Input placeholder="Telefono (opcional)" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { type: 'email', message: 'Ingrese un email valido' },
            ]}
          >
            <Input placeholder="Email (opcional)" />
          </Form.Item>

          <Form.Item
            name="address"
            label="Direccion"
          >
            <TextArea rows={2} placeholder="Direccion (opcional)" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notas"
          >
            <TextArea rows={2} placeholder="Notas adicionales (opcional)" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Activo"
            valuePropName="checked"
          >
            <Switch checkedChildren="Si" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SuppliersTab;
